/** The scene: lights, starry space and the planet's halo, and the groups everything hangs in. */
import * as THREE from 'three';

import { createStars, SPACE, type Stars } from './stars';

export interface WorldScene {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  sun: THREE.DirectionalLight;
  /** Rotates under you: the planet surface, props and crewmates. */
  planet: THREE.Group;
  props: THREE.Group;
  others: THREE.Group;
  sky: THREE.Group;
  /** Behind everything, turning with the planet. */
  stars: Stars;
  halo: THREE.Sprite;
}

/** A round gradient on a small canvas: the halo, soft contact shadows. */
export function radialTexture(stops: [number, string][]): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    for (const [offset, color] of stops) gradient.addColorStop(offset, color);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export const softMaterial = (
  color: number,
  extra?: THREE.MeshStandardMaterialParameters,
): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.82, metalness: 0, ...extra });

export function createScene(): WorldScene {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(SPACE);
  const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 800);
  const stars = createStars();
  scene.add(stars.points);

  scene.add(new THREE.HemisphereLight(0xf6f8ff, 0xc9c2b6, 1.45));
  const sun = new THREE.DirectionalLight(0xffffff, 2.5);
  sun.position.set(-5, 11, 7);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.03;
  sun.shadow.radius = 4;
  scene.add(sun, sun.target);
  const fill = new THREE.DirectionalLight(0xdce8ff, 0.55);
  fill.position.set(7, 1, -5);
  scene.add(fill);

  const planet = new THREE.Group();
  const props = new THREE.Group();
  const others = new THREE.Group();
  planet.add(props, others);
  scene.add(planet);
  const sky = new THREE.Group();
  scene.add(sky);

  const halo = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: radialTexture([
        [0, 'rgba(150,190,240,0)'],
        [0.56, 'rgba(150,190,240,0)'],
        [0.635, 'rgba(150,190,240,.42)'],
        [0.72, 'rgba(170,205,245,.16)'],
        [0.86, 'rgba(190,215,248,.05)'],
        [1, 'rgba(200,220,250,0)'],
      ]),
      depthWrite: false,
      transparent: true,
    }),
  );
  scene.add(halo);

  return { scene, camera, sun, planet, props, others, sky, stars, halo };
}
